/*
  carousel-ada-home-banner.js
  ----------------------------
  Accessibility enhancements for the homepage promotional banner carousel
  (.home-carousel-container, rendered by snippets/index-banner.liquid).

  What it was missing:

    1. Off-screen slides stayed fully focusable and screen-reader-discoverable
       at all times (every slide's link was in the tab order, and every
       slide's content was exposed to a screen reader's virtual cursor,
       regardless of which slide was actually visible). That's a real
       keyboard/focus-order bug, not just a nice-to-have.
    2. No carousel/slide semantics (WAI-ARIA carousel pattern).
    3. No confirmation to screen reader users that a manual slide change
       actually happened (nothing tells them the arrow/dot click worked).
    4. Autoplay had no pause/play treatment (WCAG 2.2.2 Pause, Stop, Hide).

  index-banner.liquid already has its own inline script that owns slide
  navigation (updateSlide/arrows/dots) and the autoplay timer itself, exposing
  start/stop/goTo via window.__homeCarouselAutoplay. This file doesn't touch
  that DOM directly -- it enhances the same DOM from the outside and drives
  play/pause through that handle, the same "navigation stays local, play/pause
  state comes from the shared core" split used for the Judge.me carousel in
  carousel-ada-jdgm.js. To know which slide is current, it reads the dot that
  script already marks with aria-current="true" rather than duplicating that
  state.
*/
document.addEventListener('DOMContentLoaded', function () {
  const container = document.querySelector('.home-carousel-container');
  if (!container) return;

  const slides = container.querySelectorAll('.home-carousel-item');
  const dots = container.querySelectorAll('.home-carousel-dot');
  const dotsWrapper = container.querySelector('.home-carousel-dots');

  if (slides.length < 2) return;

  container.setAttribute('role', 'region');
  container.setAttribute('aria-roledescription', 'carousel');
  container.setAttribute('aria-label', 'Promotional banner');
  slides.forEach((slide, i) => {
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', `Slide ${i + 1} of ${slides.length}`);
  });

  const controller = window.CarouselADA
    ? window.CarouselADA.createController({
        playingMessage: 'Promotional banner playing',
        pausedMessage: 'Promotional banner paused',
        onPlay: () => window.__homeCarouselAutoplay && window.__homeCarouselAutoplay.start(),
        onPause: () => window.__homeCarouselAutoplay && window.__homeCarouselAutoplay.stop(),
      })
    : null;
  const live = controller ? { announce: controller.announce } : null;
  if (controller) container.appendChild(controller.liveRegion);

  const applyActive = (index, opts) => {
    opts = opts || {};
    slides.forEach((slide, i) => {
      const active = i === index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      const link = slide.querySelector('a');
      if (link) link.tabIndex = active ? 0 : -1;
    });
    // Only announce a slide the visitor moved to. Announcing every autoplay
    // advance makes a screen reader talk over the rest of the page.
    if (live && !opts.silent && !controller.isPlaying()) {
      const img = slides[index].querySelector('img');
      const alt = img && img.getAttribute('alt');
      live.announce(`Slide ${index + 1} of ${slides.length}${alt ? ': ' + alt : ''}`);
    }
  };

  const currentIndex = () => {
    let current = 0;
    dots.forEach((dot, i) => {
      if (dot.getAttribute('aria-current') === 'true') current = i;
    });
    return current;
  };

  applyActive(currentIndex(), { silent: true }); // set initial state, no announcement on load

  if (dotsWrapper) {
    new MutationObserver(() => applyActive(currentIndex())).observe(dotsWrapper, {
      attributes: true,
      attributeFilter: ['aria-current'],
      subtree: true,
    });
  }

  if (controller && window.CarouselADA) {
    // Manual nav (arrows/dots) pauses autoplay rather than fighting it: a click
    // mid-rotation that gets silently overridden by the next auto-advance a
    // moment later reads as broken, and index-banner.liquid's arrows/dots have
    // no "resume" affordance of their own, so pausing (not a timed re-freeze
    // like jdgm's arrow-only trick) is the correct outcome here -- the visible
    // play/pause button is how a visitor resumes if they want to.
    const onManualNav = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest('.home-carousel-arrow, .home-carousel-dot')) {
        controller.pause();
      }
    };
    container.addEventListener('click', onManualNav, true);

    const button = window.CarouselADA.createPausePlayButton({
      onToggle: controller.toggle,
      playLabel: 'Play promotional banner',
      pauseLabel: 'Pause promotional banner',
    });
    controller.setButton(button);
    // Appended to the container itself, NOT dotsWrapper: .home-carousel-dots is
    // its own position:absolute box pinned to the bottom edge, so the button's
    // top/right (see .home-carousel-container .carousel-ada-button in
    // global-styles.css) would resolve against THAT box's corner instead of the
    // banner's -- landing bottom-right and clipped by the dots bar instead of
    // top-right of the banner.
    container.appendChild(button);

    window.CarouselADA.wireFocusPause(container, controller);

    if (window.CarouselADA.prefersReducedMotion()) {
      controller.pause({ silent: true });
    } else {
      // NOT controller.play(): createController() defaults isPlaying to true,
      // so play() would see it's "already playing" and return before ever
      // calling onPlay() -- the button would show a playing state while the
      // interval never actually starts. Start the timer directly instead,
      // matching what play()'s onPlay callback would have done.
      window.__homeCarouselAutoplay && window.__homeCarouselAutoplay.start();
    }
  }
});
